import { Link } from "react-router-dom";
import HeroBreadcrumb from "@/components/layout/HeroBreadcrumb";
import {
  Download,
  Mail,
  Phone,
  ArrowRight,
  MapPin,
  FileText,
  Users,
} from "lucide-react";

const ATTIVITA = [
  {
    eyebrow: "Sviluppo e nuova imprenditoria",
    titolo: "Sosteniamo nascita e crescita delle imprese",
    desc: "Favoriamo lo sviluppo dei soci, promuoviamo nuova imprenditoria e gestiamo le fasi di start-up con servizi dedicati: dalla pianificazione iniziale al consolidamento sul mercato. Accompagniamo gli aspiranti imprenditori in ogni fase del percorso, dalla validazione dell'idea di business al business plan, fino all'avvio operativo. Mettiamo a disposizione consulenza, formazione e una rete di partner per agevolare l'accesso al credito, ai bandi e alle opportunità di crescita.",
    img: "https://images.unsplash.com/photo-1557804506-669a67965ba0?w=1280&q=70&auto=format",
    alt: "Team di neo-imprenditori pianifica il proprio progetto di start-up alla lavagna",
  },
  {
    eyebrow: "Assistenza alle imprese",
    titolo: "Procedure amministrative semplificate",
    desc: "Forniamo assistenza e consulenza per le procedure amministrative relative all'accesso e allo svolgimento dell'attività, garantendo un rapporto agevole con la Pubblica Amministrazione. Ci occupiamo di pratiche SUAP, autorizzazioni commerciali, licenze, comunicazioni alle autorità competenti e adempimenti normativi. Sgraviamo le imprese dai costi e dai tempi della burocrazia, permettendo agli imprenditori di concentrarsi sul proprio business.",
    img: "https://images.unsplash.com/photo-1780733064275-d1ade9b83a3d?w=1280&q=70&auto=format",
    alt: "Consulente che assiste un imprenditore nella revisione delle pratiche amministrative",
  },
  {
    eyebrow: "Reti di impresa",
    titolo: "Centri commerciali naturali e progetti di territorio",
    desc: "Promuoviamo, realizziamo e gestiamo iniziative — anche con contributi pubblici — individuando e sviluppando centri commerciali naturali e reti di impresa per rivitalizzare i centri urbani. Lavoriamo a fianco dei Comuni e delle associazioni locali per progettare interventi di rigenerazione, animazione territoriale e marketing distrettuale. Stipuliamo convenzioni per l'organizzazione e gestione di attività innovative di commercio che valorizzino l'identità dei luoghi e l'attrattività turistico-commerciale.",
    img: "https://images.unsplash.com/photo-1779043508307-ec522af2d50e?w=1280&q=70&auto=format",
    alt: "Via commerciale animata con negozi ed esercizi di vicinato in un centro urbano",
  },
  {
    eyebrow: "Studi e progetti",
    titolo: "Ricerche di mercato e progettazione",
    desc: "Realizziamo studi di fattibilità, progettazione di ingegneria e ricerche di mercato in collaborazione con organismi pubblici e privati, per supportare scelte strategiche consapevoli. Elaboriamo analisi territoriali, studi socio-economici, valutazioni di impatto e modelli previsionali su domanda di consumo, comportamenti d'acquisto e dinamiche distributive. Forniamo agli enti pubblici e alle imprese gli strumenti conoscitivi necessari per pianificare investimenti, politiche commerciali e progetti di sviluppo.",
    img: "https://images.unsplash.com/photo-1560221328-12fe60f83ab8?w=1280&q=70&auto=format",
    alt: "Grafici e dati di analisi di mercato su schermo",
  },
];

const ATTIVITA_SPECIFICHE = [
  "Programmi per la promozione e il rilancio dei centri turistici e delle aree montane",
  "Promozione e gestione di reti di impresa e consorzi di operatori commerciali",
  "Elaborazione di progetti di sviluppo per specifiche categorie merceologiche",
  "Stipula di convenzioni per attività e forme innovative di commercio",
  "Funzioni conferite ai CAT dalle leggi e disposizioni della pubblica amministrazione",
];

export default function CatPage() {
  return (
    <main id="main-content" data-testid="cat-page">
      {/* Hero */}
      <section className="bg-[var(--confcom-blue)] py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <HeroBreadcrumb />
          <div>
            <h1 className="text-4xl md:text-5xl font-bold text-white font-['Encode_Sans_Semi_Condensed'] tracking-tight">
              C.A.T. Confcommercio Roma
            </h1>
            <p className="text-lg text-white/80 mt-3 max-w-3xl">
              Centro di Assistenza Tecnica Imprese per il Lazio — il punto di riferimento operativo per lo sviluppo delle imprese del commercio nella Regione.
            </p>
            <div className="mt-6 flex flex-wrap gap-3">
              <Link
                to="/servizi"
                className="inline-flex items-center gap-2 bg-white text-[var(--confcom-blue)] px-7 py-3 rounded-md font-semibold hover:opacity-90 transition-opacity text-sm"
                data-testid="cat-hero-cta-servizi"
              >
                Scopri i nostri Servizi <ArrowRight size={16} aria-hidden="true" />
              </Link>
              <a
                href="https://www.confcommercioroma.it/wp-content/uploads/2024/05/BROCHURE-CAT.pdf"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 border-2 border-white/40 text-white px-7 py-3 rounded-md font-semibold hover:opacity-90 transition-opacity text-sm"
                data-testid="cat-hero-brochure"
              >
                <Download size={16} aria-hidden="true" /> Sfoglia la brochure
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Intro */}
      <section className="py-14 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-start">
            <div>
              <p className="text-xl text-[var(--confcom-text-primary)] font-semibold font-['Encode_Sans_Semi_Condensed'] leading-relaxed mb-6">
                Il C.A.T. Confcommercio Lazio S.r.l. accompagna le imprese del commercio del Lazio nelle fasi di sviluppo, innovazione e rapporto con la Pubblica Amministrazione.
              </p>
              <p className="text-[var(--confcom-text-secondary)] leading-relaxed mb-4">
                In conformità con il <strong className="text-[var(--confcom-text-primary)]">Testo Unico del Commercio della Regione Lazio (L.R. n. 22/2019, artt. 8-69)</strong>, la società opera per favorire lo sviluppo dei soci e promuovere le loro attività.
              </p>
              <p className="text-[var(--confcom-text-secondary)] leading-relaxed mb-4">
                Promuove nuova imprenditoria gestendo le <strong className="text-[var(--confcom-text-primary)]">fasi di start-up</strong> e sostiene <strong className="text-[var(--confcom-text-primary)]">iniziative di innovazione</strong> volte al miglioramento della rete distributiva, garantendo alle imprese un rapporto agevole con la pubblica amministrazione attraverso accordi, convenzioni, avvalimento, delega, concessione ed erogazione di servizi pubblici.
              </p>
              <p className="text-[var(--confcom-text-secondary)] leading-relaxed">
                La società è <strong className="text-[var(--confcom-text-primary)]">iscritta nell'Albo regionale del Lazio dei CAT</strong> ex Determina Dirigenziale n. G02172 del 20/02/2023.
              </p>
            </div>
            <div className="flex items-center justify-center">
              <img
                src="https://images.unsplash.com/photo-1560250056-07ba64664864?w=900&q=70&auto=format"
                alt="Consulente C.A.T. che assiste il titolare di un'attività commerciale"
                className="w-full max-w-md rounded-2xl object-cover h-[360px]"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Numeri */}
      <section className="py-16 relative overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://images.unsplash.com/photo-1556761175-4b46a572b786?w=1920&q=60&auto=format"
            alt=""
            className="w-full h-full object-cover"
            aria-hidden="true"
          />
          <div className="absolute inset-0" style={{ backgroundColor: "rgba(4, 30, 66, 0.92)" }} />
        </div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
          <h2 className="text-2xl font-bold text-white font-['Encode_Sans_Semi_Condensed'] mb-10 text-center">I nostri numeri</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
            {[
              { value: "+5 mln €", label: "Contributi" },
              { value: "1.569",    label: "Imprese assistite" },
              { value: "2.757",    label: "Consulenze erogate" },
              { value: "75 mila €", label: "Bando Start Up" },
            ].map((stat) => (
              <div key={stat.label} className="bg-white/[0.08] backdrop-blur-sm border border-white/10 rounded-2xl p-8">
                <div className="text-3xl md:text-4xl font-bold text-white font-['Encode_Sans_Semi_Condensed']">{stat.value}</div>
                <div className="text-sm text-white/60 mt-2">{stat.label}</div>
              </div>
            ))}
          </div>
          <div className="flex flex-wrap justify-center gap-4 mt-10">
            <a
              href="https://www.confcommercioroma.it/wp-content/uploads/2024/05/BROCHURE-CAT.pdf"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 bg-white text-[var(--confcom-blue)] px-6 py-3 rounded-md font-medium hover:opacity-90 transition-opacity text-sm"
              data-testid="cat-numeri-brochure"
            >
              <Download size={16} aria-hidden="true" /> Sfoglia la brochure
            </a>
          </div>
        </div>
      </section>

      {/* Box Attività - layout alternato testo/foto */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-14">
            <span className="text-xs font-bold uppercase tracking-[0.2em] text-[#56B7E6] font-['Encode_Sans_Semi_Condensed']">Cosa facciamo</span>
            <h2 className="text-2xl md:text-3xl font-bold text-[var(--confcom-text-primary)] mt-3 font-['Encode_Sans_Semi_Condensed']">
              Le nostre attività
            </h2>
          </div>

          <div className="space-y-20">
            {ATTIVITA.map((a, idx) => {
              const reverse = idx % 2 === 1;
              return (
                <div
                  key={a.titolo}
                  className="grid lg:grid-cols-2 gap-12 items-center"
                  data-testid={`cat-attivita-${idx + 1}`}
                >
                  {/* Testo */}
                  <div className={reverse ? "order-1 lg:order-2" : ""}>
                    <span className="text-xs font-bold uppercase tracking-[0.2em] text-[#56B7E6] font-['Encode_Sans_Semi_Condensed']">
                      {a.eyebrow}
                    </span>
                    <h3 className="text-2xl md:text-3xl font-bold text-[var(--confcom-text-primary)] mt-3 mb-5 font-['Encode_Sans_Semi_Condensed'] tracking-tight">
                      {a.titolo}
                    </h3>
                    <p className="text-[var(--confcom-text-secondary)] leading-relaxed">
                      {a.desc}
                    </p>
                  </div>

                  {/* Foto */}
                  <div className={`rounded-2xl overflow-hidden shadow-lg ${reverse ? "order-2 lg:order-1" : ""}`}>
                    <img
                      src={a.img}
                      alt={a.alt}
                      className="w-full h-[340px] object-cover"
                      loading="lazy"
                    />
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Attività specifiche */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="mb-10">
            <span className="text-xs font-bold uppercase tracking-[0.2em] text-[#56B7E6] font-['Encode_Sans_Semi_Condensed']">Le nostre specializzazioni</span>
            <h2 className="text-2xl md:text-3xl font-bold text-[var(--confcom-text-primary)] mt-3 mb-5 font-['Encode_Sans_Semi_Condensed']">
              Attività specifiche
            </h2>
            <p className="text-[var(--confcom-text-secondary)] leading-relaxed max-w-3xl">
              Il C.A.T. Confcommercio Lazio si impegna a svolgere diverse attività dedicate al rilancio del territorio e delle categorie merceologiche, in collaborazione con la Pubblica Amministrazione.
            </p>
          </div>

          <div className="grid sm:grid-cols-2 gap-4">
            {ATTIVITA_SPECIFICHE.map((a) => (
              <div
                key={a}
                className="flex items-center gap-4 p-5 rounded-xl border border-[var(--confcom-border)]"
                data-testid={`cat-spec-${a.toLowerCase().slice(0, 30).replace(/[\s/]/g, "-")}`}
              >
                <div className="w-12 h-12 bg-[var(--confcom-bg-blue-light)] rounded-xl flex items-center justify-center shrink-0">
                  <FileText size={22} className="text-[var(--confcom-blue)]" aria-hidden="true" />
                </div>
                <span className="flex-1 text-sm font-medium text-[var(--confcom-text-primary)]">{a}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Il team */}
      <section className="py-16 bg-[#F5F8FC]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10">
            <h2 className="text-2xl md:text-3xl font-bold text-[var(--confcom-text-primary)] font-['Encode_Sans_Semi_Condensed']">
              Il team
            </h2>
          </div>

          <div className="flex flex-wrap justify-center gap-5 max-w-3xl mx-auto">
            {/* Graziano Di Gioia */}
            <div className="bg-white rounded-xl p-5 border border-[var(--confcom-border)] w-full sm:w-[280px]" data-testid="cat-team-1">
              <div className="w-full aspect-[4/3] rounded-lg mb-4 bg-[var(--confcom-bg-blue-light)] flex items-center justify-center">
                <Users size={56} className="text-[var(--confcom-blue)]/60" aria-hidden="true" />
              </div>
              <p className="font-bold text-[var(--confcom-text-primary)] font-['Encode_Sans_Semi_Condensed']">Graziano Di Gioia</p>
              <p className="text-sm text-[var(--confcom-blue)] font-medium mt-1 mb-3">Responsabile area servizi urbanistici</p>
              <div className="space-y-1.5">
                <a href="mailto:g.digioia@confcommercioroma.it" className="flex items-center gap-2 text-xs text-[var(--confcom-text-secondary)] hover:text-[var(--confcom-blue)] transition-colors">
                  <Mail size={13} className="shrink-0" aria-hidden="true" /> g.digioia@confcommercioroma.it
                </a>
                <a href="tel:+390668437369" className="flex items-center gap-2 text-xs text-[var(--confcom-text-secondary)] hover:text-[var(--confcom-blue)] transition-colors">
                  <Phone size={13} className="shrink-0" aria-hidden="true" /> 06.68437369
                </a>
              </div>
            </div>

            {/* Andrea Quartullo */}
            <div className="bg-white rounded-xl p-5 border border-[var(--confcom-border)] w-full sm:w-[280px]" data-testid="cat-team-2">
              <div className="w-full aspect-[4/3] rounded-lg mb-4 bg-[var(--confcom-bg-blue-light)] flex items-center justify-center">
                <Users size={56} className="text-[var(--confcom-blue)]/60" aria-hidden="true" />
              </div>
              <p className="font-bold text-[var(--confcom-text-primary)] font-['Encode_Sans_Semi_Condensed']">Andrea Quartullo</p>
              <p className="text-sm text-[var(--confcom-blue)] font-medium mt-1 mb-3">Referente servizi Cat</p>
              <div className="space-y-1.5">
                <a href="mailto:a.quartullo@confcommercioroma.it" className="flex items-center gap-2 text-xs text-[var(--confcom-text-secondary)] hover:text-[var(--confcom-blue)] transition-colors">
                  <Mail size={13} className="shrink-0" aria-hidden="true" /> a.quartullo@confcommercioroma.it
                </a>
                <a href="tel:+390668437615" className="flex items-center gap-2 text-xs text-[var(--confcom-text-secondary)] hover:text-[var(--confcom-blue)] transition-colors">
                  <Phone size={13} className="shrink-0" aria-hidden="true" /> 06.68437615
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Certificazione */}
      <section className="py-16 bg-[#EBF5FF]">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <span className="text-xs font-bold uppercase tracking-[0.2em] text-[#56B7E6] font-['Encode_Sans_Semi_Condensed']">Certificazione</span>
          <h2 className="text-2xl md:text-3xl font-bold text-[var(--confcom-text-primary)] mt-3 mb-4 font-['Encode_Sans_Semi_Condensed'] tracking-tight">
            UNI EN ISO 9001:2015
          </h2>
          <p className="text-[var(--confcom-text-secondary)] leading-relaxed mb-8">
            Il <strong className="text-[var(--confcom-text-primary)]">C.A.T. Confcommercio Lazio S.r.l.</strong> è certificato per la qualità secondo le norme <strong className="text-[var(--confcom-text-primary)]">UNI EN ISO 9001:2015</strong>.
          </p>
          <div className="inline-flex bg-white rounded-xl shadow-sm border border-[var(--confcom-border)] p-6 items-center justify-center">
            <img
              src="https://www.confcommercioroma.it/wp-content/uploads/2017/06/uniter-bluaccredia.jpg"
              alt="Certificazione UNI EN ISO 9001 — Accredia Uniter"
              className="w-40 md:w-48 h-auto object-contain"
              loading="lazy"
            />
          </div>
        </div>
      </section>

      {/* Contattaci — stile identico Promo.Ter */}
      <section className="py-16 relative overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://images.unsplash.com/photo-1556761175-4b46a572b786?w=1920&q=60&auto=format"
            alt=""
            className="w-full h-full object-cover"
            aria-hidden="true"
          />
          <div className="absolute inset-0" style={{ backgroundColor: "rgba(0, 59, 117, 0.92)" }} />
        </div>
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 relative">
          <div className="text-center mb-10">
            <span className="text-xs font-bold uppercase tracking-[0.2em] text-[#56B7E6] font-['Encode_Sans_Semi_Condensed']">Per maggiori informazioni</span>
            <h2 className="text-3xl md:text-4xl font-bold text-white font-['Encode_Sans_Semi_Condensed'] tracking-tight mt-3">Contattaci</h2>
          </div>

          <div className="grid sm:grid-cols-1 gap-6 mb-10 max-w-2xl mx-auto">
            {/* Sede Legale e Operativa */}
            <div className="bg-white/10 backdrop-blur-sm border border-white/20 rounded-2xl p-6 text-center">
              <div className="flex items-center justify-center gap-2 mb-4">
                <MapPin size={16} className="text-[#56B7E6] shrink-0" aria-hidden="true" />
                <h3 className="text-xs font-bold uppercase tracking-[0.15em] text-[#56B7E6] font-['Encode_Sans_Semi_Condensed']">Sede Legale e Operativa</h3>
              </div>
              <p className="text-white/90 text-sm leading-relaxed mb-3">Via Marco e Marcelliano 45 – 00147 Roma</p>
              <div className="space-y-1.5 text-sm">
                <div className="flex items-center justify-center gap-2 text-white/80">
                  <Phone size={13} className="text-[#56B7E6] shrink-0" aria-hidden="true" />
                  <a href="tel:+390668437500" className="hover:text-white transition-colors">Tel: 06 68437500</a>
                </div>
                <div className="flex items-center justify-center gap-2 text-white/80">
                  <Mail size={13} className="text-[#56B7E6] shrink-0" aria-hidden="true" />
                  <a href="mailto:cat@confcommercioroma.it" className="hover:text-white transition-colors">cat@confcommercioroma.it</a>
                </div>
              </div>
            </div>
          </div>

          <div className="flex flex-wrap justify-center gap-4">
            <Link to="/contatti" className="inline-flex items-center gap-2 bg-white text-[var(--confcom-blue)] px-8 py-4 rounded-md font-bold hover:opacity-90 transition-opacity shadow-lg" data-testid="cat-cta-ricontattato">
              <ArrowRight size={18} aria-hidden="true" /> Vuoi essere ricontattato?
            </Link>
            <a href="https://www.confcommercioroma.it/wp-content/uploads/2024/05/BROCHURE-CAT.pdf" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 border-2 border-white/40 text-white px-8 py-4 rounded-md font-semibold hover:opacity-90 transition-opacity" data-testid="cat-cta-brochure-contatti">
              <Download size={18} aria-hidden="true" /> Sfoglia la brochure
            </a>
          </div>
        </div>
      </section>

      {/* Contenuti collegati */}
    </main>
  );
}
